import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Admin, AdminLoginCredentials } from '@app-types/admin-auth';
import {
  useAdminSiteGetUser,
  useAdminSiteLogin,
  useAdminSiteLogout,
} from '@/hooks/admin/useAdminSiteAuth';
import { selectAdminLogoutTransport } from '@/hooks/admin/adminSiteLogout';
import { logoutSsoContext } from '@/features/sso/ssoLogout';
import {
  captureActiveSessionAuthGeneration,
  emitActiveSessionAuthGeneration,
  isActiveSessionAuthGenerationCurrent,
  settleActiveSessionSyncCooldown,
} from '@/features/sso/activeSessionSync';
import {
  canRecoverAnonymousP1AdminMutation,
  runP1SsoAuthMutation,
} from '@/features/sso/ssoAuthMutationLock';
import {
  clearPortalSession,
  clearSsoSession,
  hasSsoSession,
  hasPortalCookieSession,
  isSsoEnabled,
  clearBrokerSessionRepairAttempt,
  isSsoSite,
  markBrokerSessionRepairAttempt,
  readActivePortalAuthChannel,
  selectExactPortalBearerToken,
  shouldAttemptBrokerSessionRepair,
  shouldNavigateAfterPortalLogin,
  validateSsoLogoutUrl,
} from '@/features/sso/ssoSession';

const SUPPRESS_FORCE_LOGOUT_TOAST_ONCE_KEY = 'suppress_force_logout_toast_once';

const isP1SsoAuthMutationEnabled = (): boolean =>
  [
    import.meta.env.VITE_SSO_ENABLED,
    import.meta.env.VITE_SSO_PASSIVE_ENABLED,
    import.meta.env.VITE_SSO_ACTIVE_SESSION_SYNC_ENABLED,
  ].some(isSsoEnabled);

interface SiteAdminAuthContextType {
  admin: Admin | null;
  currentSite: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: AdminLoginCredentials, site: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

// Keep a single stable context reference across Vite HMR reloads.
// Without this, each HMR cycle calls createContext() again, producing a new object.
// The old SiteAdminAuthProvider then provides the OLD context while consumers hold
// the NEW reference, so useContext() returns undefined and useSiteAdminAuth throws.
declare global {
  // eslint-disable-next-line no-var
  var __SiteAdminAuthContext: React.Context<SiteAdminAuthContextType | undefined> | undefined;
}
const SiteAdminAuthContext: React.Context<SiteAdminAuthContextType | undefined> =
  globalThis.__SiteAdminAuthContext ??
  (globalThis.__SiteAdminAuthContext = createContext<SiteAdminAuthContextType | undefined>(
    undefined
  ));

export const SiteAdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [currentSite, setCurrentSite] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get site from URL params for initialization
  const { site: urlSite } = useParams<{ site: string }>();

  const storedSite = useMemo(() => localStorage.getItem('site_admin_site'), []);
  const storedToken = useMemo(() => localStorage.getItem('site_admin_token'), []);
  const effectiveSite = storedSite || urlSite || null;
  const ssoSite =
    effectiveSite && isSsoSite(effectiveSite) ? effectiveSite : undefined;
  const hasCookieSession =
    ssoSite !== undefined &&
    hasPortalCookieSession(
      { site: ssoSite, entryType: 'admin' },
      localStorage,
      sessionStorage
    );

  const loginMutation = useAdminSiteLogin();
  const logoutMutation = useAdminSiteLogout();
  const queryClient = useQueryClient();
  const adminQuery = useAdminSiteGetUser(effectiveSite || '');

  // Sync admin state from server when token + site exist
  useEffect(() => {
    if ((!storedToken && !hasCookieSession) || !effectiveSite) {
      setAdmin(null);
      setCurrentSite(effectiveSite);
      setIsLoading(false);
      return;
    }

    if (adminQuery.isSuccess) {
      const adminData = adminQuery.data?.data?.admin;
      if (adminQuery.data?.success && adminData) {
        const canAccess =
          adminData.is_full_admin ||
          adminData.manage_site === effectiveSite ||
          (adminData.is_family_admin && adminData.family_site_types?.includes(effectiveSite ?? ''));
        if (canAccess) {
          const repairUrl = adminQuery.data.data?.sso_establish_url;
          // Chuyến vá này là điều hướng TOÀN TRANG ra broker rồi quay lại. Nếu nó không thật sự
          // khôi phục được phiên chung — ví dụ SLO ở portal khác vừa xoá cookie medix_sso_sid —
          // thì `/admin/auth/user` sẽ lại trả establish_url ở vòng sau và màn hình lặp vô hạn.
          // `shouldAttemptBrokerSessionRepair` giới hạn mỗi cửa sổ một lần; hết lượt thì đi tiếp
          // xuống dưới và hiển thị màn hình bình thường thay vì treo.
          const repairContext =
            effectiveSite && isSsoSite(effectiveSite)
              ? { site: effectiveSite, entryType: 'admin' as const }
              : null;

          if (
            repairContext &&
            shouldAttemptBrokerSessionRepair({
              context: repairContext,
              establishUrl: repairUrl,
              storage: sessionStorage,
            })
          ) {
            markBrokerSessionRepairAttempt(repairContext, sessionStorage);
            window.location.replace(repairUrl as string);
            return;
          }
          setAdmin(adminData);
          setCurrentSite(effectiveSite);
          if (!storedSite) {
            localStorage.setItem('site_admin_site', effectiveSite);
          }
        } else {
          localStorage.removeItem('site_admin_token');
          localStorage.removeItem('site_admin');
          localStorage.removeItem('site_admin_site');
          if (isSsoSite(effectiveSite)) {
            clearPortalSession(
              { site: effectiveSite, entryType: 'admin' },
              localStorage,
              sessionStorage
            );
          }
          setAdmin(null);
          setCurrentSite(null);
        }
      } else {
        localStorage.removeItem('site_admin_token');
        localStorage.removeItem('site_admin');
        localStorage.removeItem('site_admin_site');
        setAdmin(null);
        setCurrentSite(null);
      }
      setIsLoading(false);
      return;
    }

    if (adminQuery.isError) {
      localStorage.removeItem('site_admin_token');
      localStorage.removeItem('site_admin');
      localStorage.removeItem('site_admin_site');
      if (isSsoSite(effectiveSite)) {
        clearPortalSession(
          { site: effectiveSite, entryType: 'admin' },
          localStorage,
          sessionStorage
        );
      }
      setAdmin(null);
      setCurrentSite(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
  }, [
    adminQuery.isSuccess,
    adminQuery.isError,
    adminQuery.data,
    storedToken,
    effectiveSite,
    storedSite,
    hasCookieSession,
  ]);

  const login = useCallback(
    async (credentials: AdminLoginCredentials, site: string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      const context = isSsoSite(site)
        ? { site, entryType: 'admin' } as const
        : undefined;
      const ssoEnabled = context !== undefined && isP1SsoAuthMutationEnabled();
      const performLogin = async (): Promise<boolean> => {
        try {
          const response = await loginMutation.mutateAsync({ credentials, site });

          if (response.success && response.data) {
            if (context) {
              clearSsoSession(context);
            }
            sessionStorage.removeItem(SUPPRESS_FORCE_LOGOUT_TOAST_ONCE_KEY);
            // Đăng nhập lại là một ý định mới của người dùng, nên trả lại quyền thử vá phiên chung
            // — đừng để dấu chống-lặp của lần hỏng trước chặn mất chuyến vá hợp lệ lần này.
            if (isSsoSite(site)) {
              clearBrokerSessionRepairAttempt(
                { site, entryType: 'admin' },
                sessionStorage,
              );
            }
            setAdmin(response.data.admin);
            setCurrentSite(site);
            if (ssoEnabled && context) {
              emitActiveSessionAuthGeneration(context, localStorage, window);
              // Same reason as the clinic sign-in: the session this login established is the
              // one the landing page should use, so suppress the boot-time sync that would
              // otherwise make the admin screen flash through the broker once more.
              settleActiveSessionSyncCooldown(context, sessionStorage, localStorage);
            }

            // Cross-Portal source (DEC-023): only present when this login came via the hidden Cognito
            // fallback. One hidden top-level round-trip to the broker sets the central medix_sso_sid
            // cookie, then 303-redirects back to the admin app (token already stored by the mutation).
            const establishUrl = (response.data as { sso_establish_url?: string }).sso_establish_url;
            if (!shouldNavigateAfterPortalLogin(establishUrl)) {
              window.location.replace(establishUrl as string);
              return false;
            }
            setIsLoading(false);
            return true;
          }

          setError(response.message || 'ログインに失敗しました。');
          setIsLoading(false);
          return false;
        } catch (err: unknown) {
          if (err && typeof err === 'object' && 'response' in err) {
            const axiosError = err as { response?: { data?: { message?: string } } };
            setError(axiosError.response?.data?.message || 'ログインに失敗しました。');
          } else {
            setError('ログインに失敗しました。');
          }
          setIsLoading(false);
          return false;
        }
      };

      if (!context) {
        return performLogin();
      }

      try {
        const loginGeneration = captureActiveSessionAuthGeneration(context, localStorage);
        const result = await runP1SsoAuthMutation({
          context,
          ssoEnabled,
          storage: localStorage,
          operation: performLogin,
          // A page-load active-sync probe may commit a no-op while this click is
          // queued for the same Web Lock. The login is still safe only when the
          // exact click-time anonymous/auth generation remains current.
          canRunAfterCommittedMutation: () =>
            isActiveSessionAuthGenerationCurrent(loginGeneration, localStorage),
          canRecoverAbandonedMutation: () =>
            canRecoverAnonymousP1AdminMutation({
              context,
              authGeneration: loginGeneration,
              localStorage,
              sessionStorage,
            }),
        });
        if (result.kind === 'completed') {
          return result.value;
        }

        // Web Locks CHỈ tồn tại trong secure context, nên một deployment nội bộ chạy http
        // không hề có `navigator.locks`: mọi lần đăng nhập dừng đúng ở đây với thông báo
        // "không hoàn tất an toàn", dù CHƯA có request nào được gửi.
        //
        // Khác hẳn logout bên dưới: ở đó request có thể đã đi nên không được gửi lại. Còn ở
        // đây runP1SsoAuthMutation trả về TRƯỚC KHI gọi `operation`, nên không có gì mơ hồ để
        // phải bảo vệ — và không có lock cũng nghĩa là không tab nào khác giành được lock để
        // mà tranh. P2 chọn đúng như vậy: "Any browser that cannot serialize the mutation at
        // all still logs in."
        if (result.kind === 'skipped' && result.reason === 'locks_unavailable') {
          return performLogin();
        }
      } catch {
        // The response could not be committed safely. Keep the current session unchanged.
      }

      setError('ログインを安全に完了できませんでした。もう一度お試しください。');
      setIsLoading(false);
      return false;
    },
    [loginMutation]
  );

  const logout = useCallback(async () => {
    const siteForApi = currentSite || localStorage.getItem('site_admin_site') || urlSite;
    const siteForRedirect = urlSite || currentSite || localStorage.getItem('site_admin_site');
    const portalContext = siteForRedirect && isSsoSite(siteForRedirect)
      ? { site: siteForRedirect, entryType: 'admin' } as const
      : undefined;
    const activeChannel = portalContext
      ? readActivePortalAuthChannel(portalContext.site, localStorage)
      : null;
    const hasAuthoritativeCookie = portalContext !== undefined &&
      activeChannel?.entryType === 'admin' && activeChannel.transport === 'cookie';
    const hasLegacyCookie = portalContext !== undefined && hasSsoSession(portalContext);
    const exactBearerToken = hasAuthoritativeCookie || hasLegacyCookie
      ? null
      : portalContext
        ? selectExactPortalBearerToken(portalContext, localStorage)
        : localStorage.getItem('site_admin_token');
    const hasBearerToken = exactBearerToken !== null;
    const ssoContext = portalContext &&
      (hasLegacyCookie || hasAuthoritativeCookie)
        ? portalContext
        : undefined;
    const logoutTransport = selectAdminLogoutTransport({
      hasBearerToken,
      hasCookieSession: ssoContext !== undefined,
    });
    const generationSite = siteForApi || siteForRedirect;
    const generationContext = generationSite && isSsoSite(generationSite)
      ? { site: generationSite, entryType: 'admin' } as const
      : undefined;
    const logoutGeneration = generationContext
      ? captureActiveSessionAuthGeneration(generationContext, localStorage)
      : undefined;
    const ssoEnabled = generationContext !== undefined && isP1SsoAuthMutationEnabled();
    let redirectUrl = siteForRedirect ? `/${siteForRedirect}/admin/login` : '/';

    const isLogoutIntentCurrent = (): boolean => {
      try {
        if (
          logoutTransport === 'bearer' &&
          localStorage.getItem('site_admin_token') !== exactBearerToken
        ) {
          return false;
        }

        return logoutGeneration === undefined ||
          isActiveSessionAuthGenerationCurrent(logoutGeneration, localStorage);
      } catch {
        return false;
      }
    };

    const finalizeLogout = (): boolean => {
      if (!isLogoutIntentCurrent()) {
        return false;
      }

      sessionStorage.setItem(SUPPRESS_FORCE_LOGOUT_TOAST_ONCE_KEY, '1');
      if (generationContext) {
        clearPortalSession(generationContext, localStorage, sessionStorage);
      } else {
        localStorage.removeItem('site_admin_token');
        localStorage.removeItem('site_admin');
        localStorage.removeItem('site_admin_site');
      }
      setAdmin(null);
      setCurrentSite(null);
      setError(null);
      queryClient.clear();
      if (ssoEnabled && generationContext) {
        emitActiveSessionAuthGeneration(generationContext, localStorage, window);
      }
      window.location.href = redirectUrl;

      return true;
    };

    const performLogout = async (): Promise<void> => {
      try {
        if (siteForApi && logoutTransport === 'bearer') {
          const response = await logoutMutation.mutateAsync({
            site: siteForApi,
            exactBearerToken,
          });
          redirectUrl =
            validateSsoLogoutUrl(
              response.data?.logout_url,
              siteForRedirect && isSsoSite(siteForRedirect)
                ? { site: siteForRedirect, entryType: 'admin' }
                : undefined,
            ) ?? redirectUrl;
        } else if (ssoContext && logoutTransport === 'cookie') {
          redirectUrl = await logoutSsoContext(ssoContext);
        } else if (siteForApi) {
          await logoutMutation.mutateAsync({
            site: siteForApi,
            exactBearerToken,
          });
        }
      } finally {
        finalizeLogout();
      }
    };

    if (!generationContext) {
      await performLogout();
      return;
    }

    try {
      const result = await runP1SsoAuthMutation({
        context: generationContext,
        ssoEnabled,
        storage: localStorage,
        operation: performLogout,
        canRunAfterCommittedMutation: () =>
          logoutTransport === 'bearer' &&
          localStorage.getItem('site_admin_token') === exactBearerToken,
      });

      // If the browser cannot establish the coordination primitive, do not send
      // an uncoordinated second request. A still-current exact intent can still
      // end the local protected shell; a stale X intent must preserve Y.
      if (
        result.kind === 'skipped' &&
        (result.reason === 'locks_unavailable' || result.reason === 'lock_failed')
      ) {
        if (!finalizeLogout()) {
          setError('ログアウトを安全に完了できませんでした。もう一度お試しください。');
        }
      } else if (result.kind === 'skipped' && result.reason === 'storage_unavailable') {
        setError('ログアウトを安全に完了できませんでした。もう一度お試しください。');
      }
    } catch {
      // performLogout already finalizes X when it is still current. Never start
      // a second, uncoordinated request after an ambiguous response.
    }
  }, [currentSite, urlSite, logoutMutation, queryClient]);

  return (
    <SiteAdminAuthContext.Provider
      value={{
        admin,
        currentSite,
        isAuthenticated: !!admin,
        isLoading,
        error,
        login,
        logout,
      }}
    >
      {children}
    </SiteAdminAuthContext.Provider>
  );
};

export const useSiteAdminAuth = (): SiteAdminAuthContextType => {
  const context = useContext(SiteAdminAuthContext);
  if (!context) {
    throw new Error('useSiteAdminAuth must be used within a SiteAdminAuthProvider');
  }
  return context;
};

/**
 * Safe variant that returns undefined when called outside SiteAdminAuthProvider,
 * instead of throwing. Use this in shared components that may render in both admin
 * and non-admin contexts (e.g. AddressSearchModal).
 */
export const useOptionalSiteAdminAuth = (): SiteAdminAuthContextType | undefined => {
  return useContext(SiteAdminAuthContext);
};

/**
 * Hook to check if site admin can access the current site
 */
export const useSiteAdminAccess = (): { canAccess: boolean; isLoading: boolean } => {
  const { admin, isLoading } = useSiteAdminAuth();
  const { site } = useParams<{ site: string }>();

  if (isLoading) {
    return { canAccess: false, isLoading: true };
  }

  if (!admin || !site) {
    return { canAccess: false, isLoading: false };
  }

  if (admin.is_full_admin) {
    return { canAccess: true, isLoading: false };
  }

  if (admin.is_family_admin && admin.family_site_types?.includes(site ?? '')) {
    return { canAccess: true, isLoading: false };
  }

  return { canAccess: admin.manage_site === site, isLoading: false };
};
